import WhiteForklift from "src/assets/icons/forklift.svg?react";
import InfoIcon from "src/assets/icons/SsqTitleIcon/info.svg?react";
import { Page1 } from "./Pages/Page1";
import {
    ConfirmDialog,
    FoodsBadge,
    FoodsPage,
    FormBaseContextProvider,
    FormTopBar,
    useFormBaseContext,
} from "@foodpilot/foods";

import { useNavigate, useLocation } from "react-router";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useUpsertIngredient } from "src/api/client-api/ingredients/useUpsertIngredient";
import { Page2 } from "./Pages/Page2";
import FormIcon from "src/assets/icons/SsqTitleIcon/form.svg?react";
import { Page3 } from "./Pages/Page3";
import { IngredientService } from "../IngredientService";
import { Ingredient } from "src/api/client-api/ingredients/useGetOneIngredient";
import { useState, useEffect, useRef } from "react";
import { IngredientFormType } from "./IngredientForm.types";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { useSaveIngredientSupplierTransports } from "src/components/Transport/IngredientSupplierTransportFields";
import { INGREDIENT_LEVEL_STAGES } from "src/api/client-api/transport/transportStages";
import { useEnabledTransportStages } from "src/api/client-api/transport/useEnabledTransportStages";
import { useSnackbar } from "src/utils/useSnackbar";
import { hasMissingRequiredIngredientSupplierTransportSelections } from "src/components/Transport/ingredientSupplierTransportPersistence";
import { useIngredientSupplierTransportSubmission } from "src/components/Transport/useIngredientSupplierTransportSubmission";
import {
    ConfirmationModalData,
    DataDeletionWarningDialog,
} from "src/components/Dialog/Templates/DataDeletionWarningDialog";
import { SsqPostChangedWarnings } from "src/components/Dialog/Templates/AllWarnings/SsqPostChangedWarnings";
import { AssociatedStructureWarning } from "src/components/Dialog/Templates/AllWarnings/AssociatedStructureWarning";
// EntityTag import removed - legacy system no longer used

type IngredientFormProps = {
    existingIngredient?: Ingredient;
    startingPageId: string;
};

export const IngredientForm = (props: IngredientFormProps) => {
    const { selectedCompany, isUserOfTypeSupplier } = useFoodpilotContext();
    const { existingIngredient, startingPageId } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const createIngredient = useUpsertIngredient();
    const saveIngredientSupplierTransports = useSaveIngredientSupplierTransports();
    const { isStageEnabled } = useEnabledTransportStages();
    const snackbar = useSnackbar();

    // Extraire l'URL de retour depuis les paramètres
    const searchParams = new URLSearchParams(location.search);
    const returnUrl = searchParams.get("returnUrl");
    const ingredientListUrl =
        returnUrl ? decodeURIComponent(returnUrl) : URLHandler.ingredient.list(selectedCompany?.id || 0);

    const formData = IngredientService.getIngredientToCreate(existingIngredient);
    const [ingredient, setIngredientState] = useState<IngredientFormType>(formData);
    const [openConfirmationModal, setOpenConfirmationModal] = useState<ConfirmationModalData>({
        isOpen: false,
        errors: null,
    });

    const shareOrganizations = ingredient.shares.map((pC) => pC.organization);
    const initEntities = useRef(shareOrganizations);
    const newItemsIds = shareOrganizations.map((entity) => {
        return entity.id;
    });
    const deletedEntities = initEntities.current
        .filter((i) => {
            const stillHasIt = newItemsIds.includes(i.id);
            return stillHasIt === false;
        })
        .map((i) => {
            return i.name;
        })
        .join(",");

    useEffect(() => {
        if (existingIngredient && ingredient.id === undefined) {
            const updatedFormData = IngredientService.getIngredientToCreate(existingIngredient);
            setIngredientState(updatedFormData);
        }
    }, [existingIngredient, ingredient.id]);

    const isNew = existingIngredient === undefined;
    const selectedCompanyId = selectedCompany?.id;
    const companyIri = selectedCompany?.["@id"];
    const notifyPersistenceError = () => {
        snackbar.setSnackbarProps({
            type: "error",
            message: t("une_erreur_est_survenue", "An error occurred"),
        });
    };
    const notifyTransportError = () => {
        snackbar.setSnackbarProps({
            type: "error",
            message: t(
                "ingredientSupplier.transport.saveError",
                "The transport circuits could not be saved. Please try again.",
            ),
        });
    };
    const submission = useIngredientSupplierTransportSubmission({
        draft: ingredient,
        setDraftState: setIngredientState,
        relationsKey: "suppliers",
        relatedResourceKey: "supplier",
        getDraftCounterpartIri: (row) => row.supplier["@id"],
        upsert: (draft) => {
            if (selectedCompanyId === undefined || companyIri === undefined) {
                return Promise.reject(new Error("No selected company."));
            }
            return createIngredient.mutateAsync({
                ingredient: IngredientService.ingredientFormToIngredientWrite(draft, companyIri),
                companyId: selectedCompanyId,
            });
        },
        saveTransportSelections: saveIngredientSupplierTransports,
        onSuccess: (persistedDraft) => {
            if (persistedDraft.id !== undefined && selectedCompanyId !== undefined) {
                navigate(URLHandler.ingredient.scores(selectedCompanyId, persistedDraft.id));
                return;
            }
            navigate(ingredientListUrl);
        },
        onExit: () => navigate(ingredientListUrl),
        onPersistenceError: notifyPersistenceError,
        onTransportError: notifyTransportError,
        onConcurrentEdit: notifyPersistenceError,
    });

    const allPages: FoodsPage[] = [
        {
            icon: <InfoIcon />,
            content: <Page1 ingredient={ingredient} setIngredient={submission.setDraft} />,
        },
        {
            title: t("ingredient.suppliers.title", "Suppliers"),
            icon: <WhiteForklift />,
            content: <Page2 ingredient={ingredient} setIngredient={submission.setDraft} />,
        },
        {
            title: t("product.form.summary_title", "Summary of data entered"),
            icon: <FormIcon />,
            content: <Page3 ingredient={ingredient} />,
        },
    ].filter((_, index) => !(index === 1 && isUserOfTypeSupplier()));

    const pageId = Number.parseInt(startingPageId);

    if (isNaN(pageId) || selectedCompany === null) {
        return (
            <Typography variant="big-bold">
                {t("ingredient.form.page_not_found", "The page you are trying to access does not exist.")}
            </Typography>
        );
    }

    const hasMissingTransportSelections = () => {
        const enabledStages = INGREDIENT_LEVEL_STAGES.filter((stage) => isStageEnabled(stage));
        if (enabledStages.length === 0) {
            return false;
        }

        return hasMissingRequiredIngredientSupplierTransportSelections(ingredient.suppliers, enabledStages);
    };

    return (
        <Box component="div" sx={{ marginLeft: "auto", marginRight: "auto", padding: "40px 52px " }}>
            <DataDeletionWarningDialog
                confirmationData={openConfirmationModal}
                setConfirmationData={setOpenConfirmationModal}
                onConfirm={() => {
                    void submission.submit();
                }}
            />
            <FormBaseContextProvider
                showSync={false}
                isLoading={submission.isSubmitting}
                startingPage={pageId}
                onClose={() => navigate(ingredientListUrl)}
                textOptions={{
                    title: (
                        <Stack flexDirection={"row"} alignItems={"center"} gap={theme.spacing(2)}>
                            <FoodsBadge
                                size={4}
                                icon={"products"}
                                boxProps={{
                                    sx: {
                                        minWidth: "48px",
                                        minHeight: "48px",
                                        borderRadius: "25px",
                                        backgroundColor: theme.custom.grey[400],
                                    },
                                }}
                            />
                            <Stack textOverflow={"clip"}>
                                <Typography variant="h3">
                                    {isNew ?
                                        t("ingredient.form.creation_title", "New ingredient creation")
                                    :   t("ingredient.form.modification_title", "Modify the ingredient")}
                                </Typography>
                            </Stack>
                        </Stack>
                    ),
                    subtitle:
                        ingredient.clientId ?
                            `${ingredient.name || ""} - ${ingredient.clientId}`
                        :   `${ingredient.name || ""}`,
                    nextPage: t("common.actions.next_step", "Next step"),
                    previousPage: t("common.actions.previous", "Previous"),
                    finalPage: t("common.actions.save", "Save"),
                }}
                pages={allPages}
                onValidate={() => {
                    const errors = [];

                    if (hasMissingTransportSelections()) {
                        snackbar.setSnackbarProps({
                            type: "error",
                            message: t(
                                "ingredientSupplier.transport.missingRequiredSuppliers",
                                "Enter the required transport circuits for each supplier.",
                            ),
                        });
                        return false;
                    }

                    const refId = existingIngredient?.ingredientReferenceId;
                    const hasSsqChanged = refId !== undefined && refId !== ingredient.ingredientReferenceId;
                    if (hasSsqChanged) {
                        errors.push(<SsqPostChangedWarnings />);
                    }
                    if (deletedEntities.length > 0) {
                        errors.push(<AssociatedStructureWarning deletedEntities={deletedEntities} />);
                    }

                    if (errors.length > 0) {
                        setOpenConfirmationModal({
                            isOpen: true,
                            errors,
                        });
                        return false;
                    }

                    return submission.submit();
                }}
                confirmExit={true}
                confirmOptions={{
                    titleText:
                        isNew ?
                            t(
                                "ingredient.form.quit_dialog.creation_confirm",
                                "Do you really wish to exit ingredient creation?",
                            )
                        :   t(
                                "ingredient.form.quit_dialog.modification_confirm",
                                "Do you really wish to exit ingredient modification?",
                            ),
                    confirmText:
                        isNew ?
                            t("common.close", "Close")
                        :   t("product.form.quit_dialog.dont_save_and_quit", "Don't save and leave"),
                    cancelText:
                        isNew ?
                            t("product.form.quit_dialog.dont_quit_creation", "Don't leave the creation")
                        :   t("product.form.quit_dialog.dont_quit_modification", "Don't leave the modification"),
                    onConfirm: submission.confirmExit,
                    onCancel: () => {
                        // Do nothing;
                    },
                }}
            >
                <ConfirmDialog />
                <FormTopBar />
                <CurrentPage />
            </FormBaseContextProvider>
        </Box>
    );
};

const CurrentPage = () => {
    const { selectedPage } = useFormBaseContext();
    return <Stack paddingTop={0}>{selectedPage ? selectedPage.content : null}</Stack>;
};
